// ---------------------------------------------------------------------------
// /campaigns/geo-targeting — OPUS-D09 / feature #72
// ---------------------------------------------------------------------------
// Route module for the geo-targeting authoring page. The D01 scaffold is
// using programmatic route assembly in `router.tsx`, so this file exposes:
//
//   * `CampaignsGeoTargetingPage` — the page component (default export + named)
//   * `createCampaignsGeoTargetingRoute(parent)` — helper that returns a
//     `createRoute` config object ready to be attached to the router tree.
//
// A later root-owned wire-up commit will add an import + `addChildren` entry
// in `router.tsx`. Until then this file is self-contained and safe to sit in
// `src/routes/` without being mounted.
// ---------------------------------------------------------------------------

import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { GeoTargetingForm } from "../features/geo-targeting/GeoTargetingForm";

/**
 * Authoring page for per-campaign geo rules.
 * Layout:
 *   - emerald → sky gradient heading
 *   - zinc-200 borders, 6-8px rounded surfaces
 *   - Inter via the scaffold CSS variables
 *   - no emojis, no competitor naming
 */
export function CampaignsGeoTargetingPage() {
	return (
		<main className="mx-auto flex min-h-screen w-full max-w-4xl flex-col gap-8 px-6 py-12">
			<header className="flex flex-col gap-3">
				<p className="text-xs font-medium uppercase tracking-[0.18em] text-zinc-500">
					Campaigns
				</p>
				<h1 className="text-brand-gradient text-4xl font-semibold tracking-tight sm:text-5xl">
					Geo targeting
				</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Restrict which countries, regions, and cities can pass a campaign.
					Pick a mode — allow for a narrow whitelist, deny for a broad blocklist —
					and compose rule tags down to ISO-3166 city precision. Rules save per
					campaign and hot-reload the edge check.
				</p>
			</header>

			<GeoTargetingForm />

			<footer className="mt-4 border-t border-zinc-200 pt-4 text-xs text-zinc-500">
				City beats region beats country. Matches are evaluated with that
				specificity on every click.
			</footer>
		</main>
	);
}

export default CampaignsGeoTargetingPage;

/**
 * Helper so the root router can wire this route in one line:
 *
 *   import { createCampaignsGeoTargetingRoute } from "./routes/campaigns.geo-targeting";
 *   const geoRoute = createCampaignsGeoTargetingRoute(rootRoute);
 *   rootRoute.addChildren([indexRoute, geoRoute]);
 *
 * Kept generic so the parent route type stays loose at the call-site.
 */
export function createCampaignsGeoTargetingRoute(parent: AnyRoute) {
	return createRoute({
		getParentRoute: () => parent,
		path: "/campaigns/geo-targeting",
		component: CampaignsGeoTargetingPage,
	});
}
